import {
  select,
  pointer,
  create,
  scaleLinear,
  type ScaleLinear,
  bisector,
  max,
  axisBottom,
  axisLeft,
  area as d3Area,
  line as d3Line,
  curveMonotoneX,
  easeCubicOut,
  zoom,
  zoomIdentity,
  type D3ZoomEvent,
} from "d3";
import type { LatencyPoint } from "./definitions/LatencyPoint";
import { calculatePercentageBelow } from "./stats";

const MARGIN = { top: 28, right: 24, bottom: 40, left: 16 };

// Only the range with meaningful signal — the long flat tail beyond ~15ms is
// data-real but visually inert, so we crop the domain to keep the peak
// legible. Zooming operates within this cropped range (scroll/pinch to zoom
// in, drag to pan once zoomed, or use the on-screen controls).
const MAX_LATENCY = 15;
const ZOOM_SCALE_EXTENT: [number, number] = [1, 10];

export interface ChartOptions {
  /** @default "Latency (ms)" */
  xAxisLabel?: string;
  /** @default "Proportion of orders" */
  yAxisLabel?: string;
  /** Hover crosshair + value tooltip. @default true */
  showTooltip?: boolean;
  /** Scroll/pinch/drag zoom + on-screen zoom controls. @default true */
  showZoom?: boolean;
  /**
   * Line stroke color. No default here, deliberately — the caller (the Lit
   * component) always resolves this from a CSS custom property
   * (`--latency-line-color` in base.css) and passes the result in, so a
   * fallback here would just be a second, easy-to-forget-to-update place
   * the same value is written.
   */
  lineColor: string;
  /** Area gradient fill color (both stops — only opacity differs). Same deal as `lineColor` — resolved from `--latency-gradient-color`. */
  gradientColor: string;
}

export function renderChart(container: HTMLElement, data: LatencyPoint[], options: ChartOptions) {
  const {
    xAxisLabel = "Latency (ms)",
    yAxisLabel = "Proportion of orders",
    showTooltip = true,
    showZoom = true,
    lineColor,
    gradientColor,
  } = options;

  const visibleData = data.filter(([t]) => t <= MAX_LATENCY);
  const bisect = bisector<LatencyPoint, number>((d) => d[0]).left;

  let transform = zoomIdentity;

  function build() {
    container.innerHTML = "";
    transform = zoomIdentity;

    const width = container.clientWidth;
    const height = container.clientHeight;
    const innerWidth = width - MARGIN.left - MARGIN.right;
    const innerHeight = height - MARGIN.top - MARGIN.bottom;
    // Container is hidden/collapsed — bail before building an SVG with
    // negative-size attributes (see the same guard in ../spread/chart.ts).
    if (innerWidth <= 0 || innerHeight <= 0) return;

    const baseX = scaleLinear().domain([2, MAX_LATENCY]).range([0, innerWidth]);

    const svg = create("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("role", "img")
      .attr(
        "aria-label",
        showZoom
          ? "Area chart of trade execution latency distribution, peaking near 2.8 milliseconds. Scroll, drag, or use the zoom controls to inspect detail."
          : "Area chart of trade execution latency distribution, peaking near 2.8 milliseconds.",
      );

    const g = svg
      .append("g")
      .attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

    // Clip the plot content so a zoomed/panned curve can't bleed past the axes.
    const clipId = "chart-plot-clip";
    svg
      .append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("width", innerWidth)
      .attr("height", innerHeight);

    const plot = g.append("g").attr("clip-path", `url(#${clipId})`);

    // --- Recessive gridlines (drawn first, behind everything) ---
    const gridY = g.append("g").attr("class", "grid grid-y");
    const gridX = g
      .append("g")
      .attr("class", "grid grid-x")
      .attr("transform", `translate(0,${innerHeight})`);

    // --- Axes ---
    const axisX = g
      .append("g")
      .attr("class", "axis axis-x")
      .attr("transform", `translate(0,${innerHeight})`);

    g.append("text")
      .attr("class", "axis-title")
      .attr("x", innerWidth / 2)
      .attr("y", innerHeight + MARGIN.bottom - 2)
      .attr("text-anchor", "middle")
      .text(xAxisLabel);

    g.append("text")
      .attr("class", "axis-title")
      .attr("x", 0)
      .attr("y", -12)
      .attr("text-anchor", "start")
      .text(yAxisLabel);

    // --- Gradient fill ---
    const gradientId = "area-fill-gradient";
    const defs = svg.append("defs");
    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", gradientColor)
      .attr("stop-opacity", 0.85);

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", gradientColor)
      .attr("stop-opacity", 0.03);

    // --- Area + line (path `d` filled in by update()) ---
    const areaPath = plot
      .append("path")
      .attr("class", "area-fill")
      .attr("fill", `url(#${gradientId})`);

    const linePath = plot
      .append("path")
      .attr("class", "area-line")
      .attr("fill", "none")
      .attr("stroke", lineColor)
      .attr("stroke-width", 2)
      .attr("stroke-linejoin", "round");

    // --- Hover layer: crosshair + tooltip ---
    const focus = g.append("g").attr("class", "focus").style("display", "none");

    focus
      .append("line")
      .attr("class", "focus-line")
      .attr("y1", 0)
      .attr("y2", innerHeight);

    focus
      .append("circle")
      .attr("class", "focus-dot")
      .attr("r", 5);

    const tooltip = select(container.parentElement).select<HTMLDivElement>(".chart-tooltip");

    // Pointer x within `hoverTarget` while it's over the plot, else null —
    // lets a zoom/pan tick re-place the tooltip under a pointer that hasn't
    // moved (wheel) or whose mousemoves d3-zoom swallows (drag).
    let hoverX: number | null = null;

    function hideHover() {
      focus.style("display", "none");
      tooltip.style("opacity", "0");
    }

    function windowedData(xMin: number, xMax: number): LatencyPoint[] {
      let start = visibleData.findIndex(([t]) => t >= xMin);
      if (start === -1) start = visibleData.length - 1;
      start = Math.max(0, start - 1);

      let end = start;
      for (let i = visibleData.length - 1; i >= 0; i--) {
        if (visibleData[i][0] <= xMax) {
          end = i;
          break;
        }
      }
      end = Math.min(visibleData.length - 1, end + 1);

      return visibleData.slice(start, end + 1);
    }

    // Re-applies the current zoom transform to every scale-dependent
    // element. Called on every zoom/pan tick, so it updates in place rather
    // than rebuilding the DOM — rebuilding mid-gesture would tear out the
    // very element d3-zoom's drag tracking is attached to.
    function update(x: ScaleLinear<number, number>, animateDraw: boolean) {
      const [xMin, xMax] = x.domain();
      const windowData = windowedData(xMin, xMax);
      const yMax = (max(windowData, (d) => d[1]) ?? max(visibleData, (d) => d[1])!) * 1.08;
      const y = scaleLinear().domain([0, yMax]).range([innerHeight, 0]);

      gridY
        .call(axisLeft(y).tickSize(-innerWidth).ticks(5).tickFormat(() => ""))
        .call((sel) => sel.select(".domain").remove());

      gridX
        .call(axisBottom(x).tickSize(-innerHeight).ticks(8).tickFormat(() => ""))
        .call((sel) => sel.select(".domain").remove());

      axisX
        .call(axisBottom(x).ticks(8).tickPadding(10))
        .call((sel) => sel.select(".domain").remove());

      const area = d3Area<LatencyPoint>()
        .x((d) => x(d[0]))
        .y0(innerHeight)
        .y1((d) => y(d[1]))
        .curve(curveMonotoneX);

      const line = d3Line<LatencyPoint>()
        .x((d) => x(d[0]))
        .y((d) => y(d[1]))
        .curve(curveMonotoneX);

      areaPath.datum(windowData).attr("d", area);
      linePath.datum(windowData).attr("d", line);

      // Draw-on animation only plays once, on first mount — replaying it on
      // every zoom/pan tick would be seasick, not polished.
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (animateDraw) {
        const node = linePath.node();
        if (node && !prefersReducedMotion) {
          const totalLength = node.getTotalLength();
          linePath
            .attr("stroke-dasharray", `${totalLength} ${totalLength}`)
            .attr("stroke-dashoffset", totalLength)
            .transition()
            .duration(1200)
            .ease(easeCubicOut)
            .attr("stroke-dashoffset", 0);
        }
      }

      return y;
    }

    let currentX = baseX;
    let currentY = update(baseX, true);

    // --- Zoom + pan ---
    const zoomBehavior = zoom<SVGRectElement, undefined>()
      .scaleExtent(ZOOM_SCALE_EXTENT)
      .translateExtent([
        [0, 0],
        [innerWidth, innerHeight],
      ])
      .extent([
        [0, 0],
        [innerWidth, innerHeight],
      ])
      .on("zoom", (event: D3ZoomEvent<SVGRectElement, undefined>) => {
        transform = event.transform;
        currentX = transform.rescaleX(baseX);
        currentY = update(currentX, false);
        const source = event.sourceEvent as Event | null;
        if (hoverX !== null && source instanceof MouseEvent) {
          hoverX = pointer(source, hoverTarget.node())[0];
        }
        if (hoverX !== null) showHoverAt(hoverX);
        else hideHover();
      });

    const hoverTarget = svg
      .append("rect")
      .attr("class", "hover-target")
      .attr("x", MARGIN.left)
      .attr("y", MARGIN.top)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("fill", "transparent");

    function zoomBy(factor: number) {
      hoverTarget.transition().duration(250).call(zoomBehavior.scaleBy, factor);
    }

    function resetZoom() {
      hoverTarget.transition().duration(300).call(zoomBehavior.transform, zoomIdentity);
    }

    if (showZoom) {
      hoverTarget
        .call(zoomBehavior)
        // Reserve double-click to reset the view instead of d3-zoom's default
        // "zoom in one step" — redundant with the buttons/scroll below. Must
        // be removed from the selection (not the behavior — it only knows
        // "start"/"zoom"/"end"/"interrupt") after .call(zoomBehavior) binds it.
        .on("dblclick.zoom", null)
        .on("dblclick", () => resetZoom());
    }

    function showHoverAt(mx: number) {
      if (!showTooltip) return;
      const latency = currentX.invert(mx - MARGIN.left);
      // See spread/chart.ts — clamping to `length - 1` left `d0`
      // undefined when only one point is visible.
      const i = bisect(visibleData, latency, 1);
      const d0 = visibleData[i - 1];
      const d1 = visibleData[i];
      const d = d1 && latency - d0[0] > d1[0] - latency ? d1 : d0;
      if (!d) return;

      const px = currentX(d[0]);
      const py = currentY(d[1]);
      focus.select(".focus-line").attr("x1", px).attr("x2", px);
      focus.select(".focus-dot").attr("cx", px).attr("cy", py);

      const cumulative = calculatePercentageBelow(data, d[0]);

      focus.style("display", null);
      tooltip.style("opacity", "1");
      tooltip.html(
        `<div class="tooltip-row"><span class="tooltip-label">Proportion:</span> <strong class="tooltip-value">${(d[1] * 100).toFixed(2)}%</strong></div>` +
          `<div class="tooltip-row"><span class="tooltip-label">Cumulative:</span> <strong class="tooltip-value">${cumulative.toFixed(2)}%</strong> <span class="tooltip-caption">in less than ${d[0].toFixed(2)}ms</span></div>`,
      );

      // Anchor just above-right of the dot, flipping left/up if it would overflow.
      const tooltipEl = tooltip.node() as HTMLDivElement;
      const tooltipWidth = tooltipEl?.offsetWidth ?? 0;
      const tooltipHeight = tooltipEl?.offsetHeight ?? 0;

      let left = MARGIN.left + px + 12;
      let top = MARGIN.top + py - tooltipHeight - 12;
      if (left + tooltipWidth > width) left = MARGIN.left + px - tooltipWidth - 12;
      if (top < 0) top = MARGIN.top + py + 12;

      tooltip.style("left", `${left}px`).style("top", `${top}px`);
    }

    if (showTooltip) {
      hoverTarget
        .on("mouseenter mousemove", (event: MouseEvent) => {
          hoverX = pointer(event, hoverTarget.node())[0];
          showHoverAt(hoverX);
        })
        .on("mouseleave", () => {
          hoverX = null;
          hideHover();
        });
    }

    // --- Zoom controls (overlaid on the top-right corner of the chart) ---
    if (showZoom) {
      const controls = select(container)
        .append("div")
        .attr("class", "chart-zoom-controls")
        .attr("role", "group")
        .attr("aria-label", "Chart zoom controls");

      controls
        .append("button")
        .attr("type", "button")
        .attr("class", "zoom-btn")
        .attr("aria-label", "Zoom in")
        .text("+")
        .on("click", () => zoomBy(1.5));

      controls
        .append("button")
        .attr("type", "button")
        .attr("class", "zoom-btn")
        .attr("aria-label", "Zoom out")
        .text("−")
        .on("click", () => zoomBy(1 / 1.5));

      controls
        .append("button")
        .attr("type", "button")
        .attr("class", "zoom-btn zoom-reset")
        .attr("aria-label", "Reset zoom")
        .text("⟲")
        .on("click", () => resetZoom());
    }

    container.appendChild(svg.node()!);
  }

  build();

  const resizeObserver = new ResizeObserver(() => build());
  resizeObserver.observe(container);

  return () => resizeObserver.disconnect();
}
